/**
 * CU-14zefhy21rx X5: the assistant writes its replies in markdown (`**bold**`, `- ` bullets, `### headings`, tables), and
 * the reply bubble showed the raw marks. This turns a reply into a small block / inline structure that the chat template
 * renders with plain Angular bindings: a safe subset only (headings, paragraphs, bullet and numbered lists, tables, code,
 * bold, italics, inline code, rules). Nothing is ever inserted as HTML, so a reply cannot inject markup or script.
 *
 * In-app links keep working: the caller's `linkify` (the chat's `splitLinks` with its real-route check) turns the
 * markdown links / same-origin URLs / bare `/paths` of each piece of text into link segments first; those are kept whole
 * through the emphasis parsing and take on the style around them.
 */

import {TextSegment} from 'app/blocks/service/assistant-context/assistant-context';

/** A run of inline text with its style; `link` = in-app path when the run is a link. */
export interface MdInline {
    text: string;
    bold?: boolean;
    italic?: boolean;
    code?: boolean;
    link?: string;
}

export type MdBlock =
    | {kind: 'p'; inlines: MdInline[]}
    | {kind: 'heading'; level: number; inlines: MdInline[]}
    | {kind: 'list'; ordered: boolean; start: number; items: MdListItem[]}
    | {kind: 'table'; header: MdInline[][]; align: Array<'left' | 'center' | 'right' | null>; rows: MdInline[][][]}
    | {kind: 'code'; text: string}
    | {kind: 'hr'};

export interface MdListItem {
    /** nesting depth (0 = top level), from the item's indentation */
    level: number;
    inlines: MdInline[];
}

/** Turns plain text into text / in-app link segments (the chat passes `splitLinks` with its route check). */
export type Linkify = (text: string) => TextSegment[];

const plainText: Linkify = (text) => (text ? [{text}] : []);

const FENCE_RE = /^\s{0,3}(```|~~~)/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const HR_RE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const BULLET_RE = /^(\s*)[-*+]\s+(.*)$/;
const ORDERED_RE = /^(\s*)(\d{1,9})[.)]\s+(.*)$/;
const TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
/** private-use characters stand in for links and escaped marks while emphasis is parsed */
const PLACEHOLDER_BASE = 0xe000;
const PLACEHOLDER_MAX = 0x1800;
const PLACEHOLDER_RE = /[-]/g;
/** inline code, ***bold italic***, **bold**, __bold__, *italic*, _italic_ (not inside snake_case words) */
const INLINE_RE =
    /`([^`\n]+)`|\*\*\*(\S(?:[\s\S]*?\S)?)\*\*\*|\*\*(\S(?:[\s\S]*?\S)?)\*\*|__(\S(?:[\s\S]*?\S)?)__|\*(\S(?:[^*\n]*?\S)?)\*|(^|[^A-Za-z0-9_])_(\S(?:[^_\n]*?\S)?)_(?![A-Za-z0-9_])/;

/** Splits a reply into markdown blocks (see the file comment). Empty text gives no blocks. */
export function parseMarkdown(text: string, linkify: Linkify = plainText): MdBlock[] {
    const blocks: MdBlock[] = [];
    if (!text) {
        return blocks;
    }
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    let paragraph: string[] = [];
    let list: {kind: 'list'; ordered: boolean; start: number; items: MdListItem[]} | null = null;
    let listItemLines: string[] = null;
    let listItemLevel = 0;
    let listIndents: number[] = [];

    const flushListItem = () => {
        if (list && listItemLines) {
            list.items.push({level: listItemLevel, inlines: parseInline(listItemLines.join('\n'), linkify)});
        }
        listItemLines = null;
    };
    const flushList = () => {
        flushListItem();
        if (list) {
            blocks.push(list);
        }
        list = null;
        listIndents = [];
    };
    const flushParagraph = () => {
        if (paragraph.length) {
            blocks.push({kind: 'p', inlines: parseInline(paragraph.join('\n'), linkify)});
        }
        paragraph = [];
    };
    const flushAll = () => {
        flushParagraph();
        flushList();
    };
    const levelOf = (indent: number): number => {
        // depth = how many shallower indents are open above this item
        while (listIndents.length && listIndents[listIndents.length - 1] > indent) {
            listIndents.pop();
        }
        if (!listIndents.length || listIndents[listIndents.length - 1] < indent) {
            listIndents.push(indent);
        }
        return Math.min(listIndents.length - 1, 4);
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (FENCE_RE.test(line)) {
            flushAll();
            const fence = line.trim().substring(0, 3);
            const code: string[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith(fence)) {
                code.push(lines[i]);
                i++;
            }
            blocks.push({kind: 'code', text: code.join('\n')});
            continue;
        }
        if (!line.trim()) {
            flushParagraph();
            flushListItem();
            // a list goes on after one blank line when the next line is another item
            const next = lines[i + 1] || '';
            if (list && !(list.ordered ? ORDERED_RE : BULLET_RE).test(next)) {
                flushList();
            }
            continue;
        }
        const heading = HEADING_RE.exec(line);
        if (heading) {
            flushAll();
            blocks.push({kind: 'heading', level: heading[1].length, inlines: parseInline(heading[2], linkify)});
            continue;
        }
        if (isTableStart(lines, i)) {
            flushAll();
            const header = splitRow(line);
            const align = splitRow(lines[i + 1]).map((cell) => {
                const c = cell.trim();
                const left = c.startsWith(':');
                const right = c.endsWith(':');
                return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
            });
            const rows: MdInline[][][] = [];
            i += 2;
            while (i < lines.length && lines[i].trim() && lines[i].indexOf('|') >= 0) {
                const cells = splitRow(lines[i]);
                rows.push(header.map((_, c) => parseInline((cells[c] || '').trim(), linkify)));
                i++;
            }
            i--;
            blocks.push({
                kind: 'table',
                header: header.map((h) => parseInline(h.trim(), linkify)),
                align: header.map((_, c) => align[c] || null),
                rows
            });
            continue;
        }
        if (HR_RE.test(line)) {
            flushAll();
            blocks.push({kind: 'hr'});
            continue;
        }
        const bullet = BULLET_RE.exec(line);
        const ordered = bullet ? null : ORDERED_RE.exec(line);
        if (bullet || ordered) {
            flushParagraph();
            const isOrdered = !!ordered;
            const indent = (bullet ? bullet[1] : ordered[1]).replace(/\t/g, '    ').length;
            // a different kind of list at the top level starts a new list; nested ones stay inside the current one
            if (list && list.ordered !== isOrdered && indent <= (listIndents[0] ?? 0)) {
                flushList();
            }
            flushListItem();
            if (!list) {
                list = {kind: 'list', ordered: isOrdered, start: isOrdered ? Number(ordered[2]) || 1 : 1, items: []};
            }
            listItemLevel = levelOf(indent);
            listItemLines = [bullet ? bullet[2] : ordered[3]];
            continue;
        }
        if (list && listItemLines) {
            // continuation of the current item
            listItemLines.push(line.trim());
            continue;
        }
        flushList();
        paragraph.push(line);
    }
    flushAll();
    return blocks;
}

function isTableStart(lines: string[], i: number): boolean {
    const line = lines[i];
    const next = lines[i + 1];
    return (
        !!next &&
        line.indexOf('|') >= 0 &&
        next.indexOf('-') >= 0 &&
        TABLE_SEPARATOR_RE.test(next) &&
        (next.indexOf('|') >= 0 || line.trim().startsWith('|'))
    );
}

/** The cells of a table row (`| a | b |` or `a | b`); `\|` stays a literal bar. */
function splitRow(line: string): string[] {
    let row = line.trim();
    if (row.startsWith('|')) {
        row = row.substring(1);
    }
    if (row.endsWith('|') && !row.endsWith('\\|')) {
        row = row.substring(0, row.length - 1);
    }
    const cells: string[] = [];
    let current = '';
    for (let i = 0; i < row.length; i++) {
        const ch = row[i];
        if (ch === '\\' && row[i + 1] === '|') {
            current += '|';
            i++;
        } else if (ch === '|') {
            cells.push(current);
            current = '';
        } else {
            current += ch;
        }
    }
    cells.push(current);
    return cells;
}

/**
 * Inline markdown of one block: links first (via `linkify`, kept whole), then inline code, bold and italics. Unmatched
 * marks ("5 * 3", a lone `**`) stay as they are; `\*` and friends are literal.
 */
export function parseInline(text: string, linkify: Linkify = plainText): MdInline[] {
    if (!text) {
        return [];
    }
    const tokens: Array<{text: string; link?: string}> = [];
    const hold = (token: {text: string; link?: string}): string => {
        if (tokens.length >= PLACEHOLDER_MAX) {
            return token.text;
        }
        tokens.push(token);
        return String.fromCharCode(PLACEHOLDER_BASE + tokens.length - 1);
    };
    // the reply's own private-use characters would be read as placeholders
    const clean = text.replace(PLACEHOLDER_RE, '');
    let held = '';
    for (const seg of linkify(clean)) {
        if (seg.link) {
            held += hold({text: seg.text, link: seg.link});
        } else {
            held += seg.text.replace(/\\([\\`*_#|[\]()~>+-])/g, (_, ch) => hold({text: ch}));
        }
    }
    const runs: MdInline[] = [];
    emphasis(held, {}, runs);
    // put the links and literal marks back, keeping the style they sit in
    const out: MdInline[] = [];
    const push = (run: MdInline) => {
        const last = out[out.length - 1];
        if (last && !last.link && !run.link && !!last.bold === !!run.bold && !!last.italic === !!run.italic && !!last.code === !!run.code) {
            last.text += run.text;
        } else if (run.text) {
            out.push(run);
        }
    };
    // a bare `/path` right inside emphasis ("**/runs/12**") only starts a word once the marks are gone: link it then
    const pushText = (run: MdInline, plain: string) => {
        if (!plain || run.code || run.link || !/(^|[\s(])\//.test(plain)) {
            push({...run, text: plain});
            return;
        }
        for (const seg of linkify(plain)) {
            push(seg.link ? {...run, text: seg.text, link: seg.link} : {...run, text: seg.text});
        }
    };
    for (const run of runs) {
        let plain = '';
        let literal = '';
        const flush = () => {
            pushText(run, plain);
            push({...run, text: literal});
            plain = '';
            literal = '';
        };
        for (const ch of run.text) {
            const code = ch.charCodeAt(0) - PLACEHOLDER_BASE;
            const token = ch.length === 1 && code >= 0 && code < tokens.length ? tokens[code] : null;
            if (!token) {
                if (literal) {
                    flush();
                }
                plain += ch;
            } else if (token.link && !run.code) {
                flush();
                push({...run, text: token.text, link: token.link});
            } else if (run.code) {
                plain += token.text;
            } else {
                // an escaped mark or a link label (already checked): never linked again
                if (plain) {
                    pushText(run, plain);
                    plain = '';
                }
                literal += token.text;
            }
        }
        flush();
    }
    return out.map((run) => {
        const clean: MdInline = {text: run.text};
        if (run.bold) {
            clean.bold = true;
        }
        if (run.italic) {
            clean.italic = true;
        }
        if (run.code) {
            clean.code = true;
        }
        if (run.link) {
            clean.link = run.link;
        }
        return clean;
    });
}

function emphasis(text: string, style: {bold?: boolean; italic?: boolean}, out: MdInline[]): void {
    let rest = text;
    while (rest) {
        const m = INLINE_RE.exec(rest);
        if (!m) {
            out.push({...style, text: rest});
            return;
        }
        let start = m.index;
        if (m[6] !== undefined) {
            start += m[6].length; // the character before `_` is plain text
        }
        if (start > 0) {
            out.push({...style, text: rest.substring(0, start)});
        }
        if (m[1] !== undefined) {
            out.push({...style, code: true, text: m[1]});
        } else if (m[2] !== undefined) {
            emphasis(m[2], {...style, bold: true, italic: true}, out);
        } else if (m[3] !== undefined || m[4] !== undefined) {
            emphasis(m[3] !== undefined ? m[3] : m[4], {...style, bold: true}, out);
        } else {
            emphasis(m[5] !== undefined ? m[5] : m[7], {...style, italic: true}, out);
        }
        rest = rest.substring(m.index + m[0].length);
    }
}
